import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useGetObjectiveTestInfo } from '../../../../services/hooks/CandidateArea/useGetObjectiveTestInfo';
import { TextXS } from '../../../../components/Text/Index';

interface AnswerSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationId: string;
}

const AnswerSheetModal: React.FC<AnswerSheetModalProps> = ({ isOpen, onClose, registrationId }) => {
  const { data, isLoading, isError } = useGetObjectiveTestInfo(registrationId, isOpen);

  const chosenQuestions = data?.objective_test_info?.chosen_questions
    ? Object.entries(data.objective_test_info.chosen_questions)
    : [];

  const pdfAttachment = data?.candidate_attachments?.find(
    (a) => a.document_name === 'Espelho da folha resposta',
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent maxW={['95%', '95%', '750px', '800px']} mx={3}>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={3}>
          <TextXS fontWeight="bold" textTransform="uppercase">
            Relatório de leitura da folha resposta {data?.objective_test_info?.secret_number ? `#${data.objective_test_info.secret_number}` : ''}
          </TextXS>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5} px={[3, 3, 6]}>
          {isLoading && (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" />
            </Flex>
          )}

          {isError && (
            <Flex justify="center" align="center" minH="200px">
              <Text color="red.500" fontWeight="bold">
                Erro ao carregar os dados. Tente novamente.
              </Text>
            </Flex>
          )}

          {data && !isLoading && (
            <Stack spacing={5}>
              {/* Dados do candidato */}
              <Stack spacing={2}>
                <Text fontWeight="bold" fontSize="13px" textTransform="uppercase" color="gray.600">
                  Dados do Candidato
                </Text>
                <Divider />
                <Grid templateColumns={['1fr', '1fr', 'repeat(2, 1fr)']} gap={2}>
                  <GridItem>
                    <TextXS>
                      <strong>Nome: </strong>
                      {data.candidate.name}
                    </TextXS>
                  </GridItem>
                  <GridItem>
                    <TextXS>
                      <strong>CPF: </strong>
                      {data.candidate.cpf}
                    </TextXS>
                  </GridItem>
                  <GridItem>
                    <TextXS>
                      <strong>Data de nascimento: </strong>
                      {data.candidate.date_of_birth}
                    </TextXS>
                  </GridItem>
                  <GridItem>
                    <TextXS>
                      <strong>Vaga/Cargo: </strong>
                      {data.vacancy.name.toUpperCase()}
                    </TextXS>
                  </GridItem>
                </Grid>
              </Stack>

              {/* Aviso de recurso */}
              <Alert status="warning" borderRadius={4} fontSize="12px">
                <AlertIcon />
                <AlertDescription>
                  Caso seja identificada qualquer inconsistência na leitura da folha de respostas ou nos dados cadastrais do candidato, deverá ser interposto, dentro do prazo previsto no cronograma do edital, o recurso contra a leitura da folha de respostas, para que a situação possa ser analisada pela Comissão.
                </AlertDescription>
              </Alert>

              {/* Respostas marcadas */}
              {chosenQuestions.length > 0 && (
                <Stack spacing={2}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="bold" fontSize="13px" textTransform="uppercase" color="gray.600">
                      Respostas Marcadas e dados da leitura
                    </Text>
                    {pdfAttachment && (
                      <TextXS
                        color="colorLink"
                        fontWeight="bold"
                        cursor="pointer"
                        onClick={async () => {
                          const response = await fetch(pdfAttachment.file_url);
                          const blob = await response.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          a.download = `espelho-folha-resposta-${data?.objective_test_info?.secret_number || 'documento'}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(blobUrl);
                        }}
                      >
                        Baixar o espelho da folha resposta
                      </TextXS>
                    )}
                  </Flex>
                  <Divider />
                  <Grid templateColumns="repeat(5, 1fr)" gap={2}>
                    {chosenQuestions.map(([question, answer]) => (
                      <GridItem key={question}>
                        <Box
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius={4}
                          p={2}
                          textAlign="center"
                          bg="gray.50"
                        >
                          <TextXS fontWeight="bold" color="gray.500" fontSize="10px">
                            {question.toUpperCase()}
                          </TextXS>
                          <Text fontWeight="bold" fontSize="14px" color="primaryColor">
                            {answer}
                          </Text>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                  <TextXS color="gray.500" mt={1}>
                    <strong>(*)</strong> Indica ausência de marcação ou marcação duplicada na folha de respostas. A questão não será pontuada.
                  </TextXS>
                </Stack>
              )}


            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnswerSheetModal;
